import { FormEvent, useEffect, useId } from 'react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai/react';
import { useTranslation } from 'next-i18next';
import { socialTokenAtom, tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { isRegisterLoadingAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { GENDER } from 'src/types/register';
import { pushEvent } from 'src/utils/event-gateway';
import { Container, LoadingIndicator, NextButton, Section, Title, WarningText as WarningTextBase } from './styles';
import type { StepProps } from './types';
const MobileSection = styled(Section)`
  margin-block-end: auto;

  ${({
  theme
}) => theme.screenSize.desktop} {
    display: none;
  }
`;
const DesktopSection = styled(Section)`
  margin-block-start: -16px;
  text-align: center;

  ${({
  theme
}) => theme.screenSize.tablet} {
    display: none;
  }
`;
const InputGroup = styled(Section)`
  padding: 8px 0;

  ${({
  theme
}) => theme.screenSize.desktop} {
    min-height: 364px;

    --gap: 16px;
  }
`;
const Input = styled.label`
  display: flex;

  > span {
    cursor: pointer;
    flex: 1;

    padding: 20px 24px;
    border-radius: 16px;

    background-color: ${({
  theme
}) => theme.color.surface2};
    color: ${({
  theme
}) => theme.color.content6};
    font-size: 20px;
    font-weight: 700;

    transition-properties: background-color, color, font-weight;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
  }

  > :checked + span {
    font-weight: 900;

    background-color: ${({
  theme
}) => theme.color.surface12};
    color: ${({
  theme
}) => theme.color.content1};
  }

  ${({
  theme
}) => theme.screenSize.desktop} {
    > span {
      background-color: ${({
  theme
}) => theme.color.surface4};
      color: ${({
  theme
}) => theme.color.content6};

      font-size: 16px;
      line-height: 22px;
    }

    > :checked + span {
      font-weight: 700;

      background-color: ${({
  theme
}) => theme.color.surface12};
      color: ${({
  theme
}) => theme.color.contentBright};
    }
  }
`;
const WarningText = styled(WarningTextBase)`
  ${({
  theme
}) => theme.screenSize.desktop} {
    align-self: center;
    max-width: 70%;
  }
`;
function GenderInput({
  defaultValue,
  value,
  label
}: {
  defaultValue?: GENDER;
  value: GENDER;
  label: string;
}) {
  const id = useId();
  return <Input htmlFor={id} data-sentry-element="Input" data-sentry-component="GenderInput" data-sentry-source-file="Gender.tsx">
      <input type='radio' name='gender' hidden value={value} id={id} required defaultChecked={value === defaultValue} />
      <span>{label}</span>
    </Input>;
}
export default function GenderStep({
  onNext,
  className
}: StepProps<GENDER>) {
  const {
    t
  } = useTranslation();
  const socialToken = useAtomValue(socialTokenAtom);
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  const isRegisterLoading = useAtomValue(isRegisterLoadingAtom);
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const gender = String(formData.get('gender')) as GENDER;
    pushEvent({
      eventName: EVENT_NAME.SIGNUP__SELECT_GENDER,
      eventType: EVENT_TYPE.SIGN_UP,
      eventParams: {
        action_category: 'action',
        tab: 'signup',
        page: 'gender',
        target: gender.toLowerCase(),
        child_gender: gender.toLowerCase()
      }
    });
    await onNext(gender);
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_GENDER_INFO,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  return <Container as='form' onSubmit={handleSubmit} className={className} data-sentry-element="Container" data-sentry-component="GenderStep" data-sentry-source-file="Gender.tsx">
      <Section data-sentry-element="Section" data-sentry-source-file="Gender.tsx">
        <Title data-sentry-element="Title" data-sentry-source-file="Gender.tsx">{t('SIGNUP__INFO_GENDER')}</Title>
      </Section>
      <InputGroup data-sentry-element="InputGroup" data-sentry-source-file="Gender.tsx">
        <GenderInput value={GENDER.FEMALE} label={t('gender_female')} defaultValue={socialToken?.gender} data-sentry-element="GenderInput" data-sentry-source-file="Gender.tsx" />
        <GenderInput value={GENDER.MALE} label={t('gender_male')} defaultValue={socialToken?.gender} data-sentry-element="GenderInput" data-sentry-source-file="Gender.tsx" />
      </InputGroup>
      <MobileSection data-sentry-element="MobileSection" data-sentry-source-file="Gender.tsx">
        <WarningText data-sentry-element="WarningText" data-sentry-source-file="Gender.tsx">{t('signup_extra_info_gender_warning')}</WarningText>
      </MobileSection>
      <Section data-sentry-element="Section" data-sentry-source-file="Gender.tsx">
        <NextButton type='submit' disabled={isRegisterLoading} data-sentry-element="NextButton" data-sentry-source-file="Gender.tsx">
          {isRegisterLoading ? <LoadingIndicator /> : t('SIGNUP__NEXT')}
        </NextButton>
      </Section>
      <DesktopSection data-sentry-element="DesktopSection" data-sentry-source-file="Gender.tsx">
        <WarningText data-sentry-element="WarningText" data-sentry-source-file="Gender.tsx">{t('signup_extra_info_gender_warning')}</WarningText>
      </DesktopSection>
    </Container>;
}